import { BookCorn, Contact, ContactDataSource, ContactRoleProducer } from '@eigtech/contacts-types'
import { useAssignContactToClaim } from '@eigtech/ui-shared-claims-lib'
import {
  getContactRoleLabel,
  useCreateContact,
  useGetBook,
  useUpdateContact,
} from '@eigtech/ui-shared-contacts'
import { FormDrawer } from '@eigtech/ui-shared-forms/shadcn'
import { DialogRootActions } from '@eigtech/ui/components/dialog'
import {
  withSheetErrorBoundary,
  WithSheetErrorBoundaryOptions,
} from '@eigtech/ui/components/error-boundary/sheet'
import { SheetProps } from '@eigtech/ui/components/sheet'
import { toast } from '@eigtech/ui/components/sonner'
import { Stack, StackSeparator } from '@eigtech/ui/components/stack'
import { TypographyH5 } from '@eigtech/ui/components/typopgraphy'
import { last } from 'lodash-es'
import { useImperativeHandle, useRef } from 'react'
import { AddressFieldArray } from './AddressField'
import {
  ContactForm,
  ContactFormInputField,
  useContactForm,
} from './ContactFormSchema/ContactFormSchema'
import { EmailFieldArray } from './EmailField'
import { PhoneFieldArray } from './PhoneField'
import { RoleFieldArray } from './RoleField'
import { getDefaultRoleFromBookName } from './utils/getDefaultRoleFromBookName'
import { mapApiContactToFormContact, mapFormContactToApiContactRequest } from './utils/mapContact'

export type ContactFormDrawerProps = SheetProps & {
  bookId?: BookCorn
  requiredRole?: ContactRoleProducer
} & (
    | { contact?: Partial<Contact>; dataSource: ContactDataSource }
    | { contact: Contact; dataSource?: undefined }
  )

export const ContactFormDrawer = withSheetErrorBoundary(
  function ContactFormDrawer(props: ContactFormDrawerProps) {
    const {
      actionsRef: propsActionsRef,
      bookId,
      contact,
      dataSource,
      requiredRole: propsRequiredRole,
      ...drawerProps
    } = props

    const actionsRef = useRef<DialogRootActions>(null)
    useImperativeHandle(propsActionsRef, () => actionsRef.current!)

    const { data: book } = useGetBook(bookId ?? '')

    const form = useContactForm({
      defaultValues: mapApiContactToFormContact(contact ?? {}, book),
    })

    const { mutateAsync: createContact } = useCreateContact()
    const { mutateAsync: updateContact } = useUpdateContact()
    const { mutateAsync: assignContactToClaim } = useAssignContactToClaim()

    async function onSubmit(data: ContactForm) {
      const metadata = contact?.metadata
      const request = mapFormContactToApiContactRequest(data, metadata)

      const contactId = contact?.contactId

      let updatedContact: Contact | undefined

      if (contactId) {
        await updateContact({ ...request, contactId })
      } else {
        if (!dataSource) {
          throw new Error('Missing data source')
        }
        const { contactId } = await createContact({
          ...request,
          bookId,
          metadata: { dataSource },
        })

        updatedContact = {
          ...request,
          contactId,
        }
      }

      const claimNumber = book?.metadata.type === 'claim' && bookId && last(bookId.split(':'))

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const assignments: Promise<any>[] = []

      if (!!claimNumber && !!updatedContact) {
        if (request.roles.includes('primaryContact')) {
          assignments.push(
            assignContactToClaim({
              assigneeRelationship: 'primaryContact',
              claimNumber,
              contact: updatedContact,
            })
          )
        }

        if (request.roles.includes('policyHolder')) {
          assignments.push(
            assignContactToClaim({
              assigneeRelationship: 'policyHolder',
              claimNumber,
              contact: updatedContact,
            })
          )
        }
      }

      await Promise.all(assignments)

      toast.success(`Successfully ${contact?.contactId ? 'edited' : 'created'} contact!`)
      actionsRef.current?.close()
    }

    const bookName = book?.metadata.name ?? ''
    const requiredRole = propsRequiredRole || getDefaultRoleFromBookName(bookName ?? '')

    return (
      <FormDrawer
        {...drawerProps}
        {...baseProps(props)}
        actionsRef={actionsRef}
        form={form}
        onSubmit={onSubmit}
      >
        <Stack className="gap-6" separator={<StackSeparator />}>
          <div>
            <TypographyH5 className="mb-2">Name</TypographyH5>
            <div className="grid grid-cols-2 gap-4">
              <ContactFormInputField label="First Name" name="givenName" />
              <ContactFormInputField label="Last Name" name="familyName" />
            </div>
          </div>

          <div>
            <TypographyH5 className="mb-2">Addresses</TypographyH5>
            <AddressFieldArray />
          </div>

          <div>
            <TypographyH5 className="mb-2">Contact Email(s)</TypographyH5>
            <EmailFieldArray />
          </div>

          <div>
            <TypographyH5 className="mb-2">Contact Phone Number(s)</TypographyH5>
            <PhoneFieldArray />
          </div>

          <div>
            <TypographyH5 className="mb-2">Other Info</TypographyH5>

            <Stack className="gap-4">
              <ContactFormInputField label="Description" name="description" />

              <RoleFieldArray requiredRole={requiredRole} />
            </Stack>
          </div>
        </Stack>
      </FormDrawer>
    )
  },
  (props) => baseProps(props)
)

const baseProps = (props: ContactFormDrawerProps): WithSheetErrorBoundaryOptions => ({
  title: `${props.contact?.contactId ? 'Edit' : 'Create'} ${props.requiredRole ? `${getContactRoleLabel(props.requiredRole)} ` : ''}Contact`,
})
